"use client";

import { useCallback, useEffect, useState } from "react";
import { Bell, Globe, Loader2, Shield } from "lucide-react";
import { Card, PageHeader } from "@/components/pulse-shell";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, type UserSettings } from "@/lib/api";
import { useAuthProfile } from "@/lib/auth-context";
import { displayName } from "@/lib/users-format";

export function UserSettingsPage() {
  const me = useAuthProfile();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [hideOwn, setHideOwn] = useState(false);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const out = await api.me.settings();
      setHideOwn(out.hideOwnSessionsFromOthers === true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function persist(next: Partial<UserSettings>) {
    setSaving(true);
    setError("");
    try {
      const out = await api.me.updateSettings(next);
      setHideOwn(out.hideOwnSessionsFromOthers === true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save settings");
    } finally {
      setSaving(false);
    }
  }

  const name = displayName(me?.firstName, me?.lastName, me?.email || "");

  return (
    <>
      <PageHeader
        title="User settings"
        subtitle="Preferences for your Admin account. Workspace-wide options stay under Settings."
      />
      <div className="user-settings-page">
        {error ? <div className="user-settings-error">{error}</div> : null}

        <Card className="settings-card" title="Profile">
          <div className="settings-fields">
            <label className="setting-field">
              Name
              <input className="setting-input" readOnly value={name} />
              <small>From your workspace user record. Ask an administrator to change it in Users.</small>
            </label>
            <label className="setting-field">
              Email
              <input className="setting-input" readOnly value={me?.email || ""} />
              <small>Sign-in address. Used to recognize sessions you start while logged in.</small>
            </label>
            <label className="setting-field">
              Role
              <input className="setting-input" readOnly value={me?.roleName || "—"} />
            </label>
          </div>
        </Card>

        <Card className="settings-card" title="Sessions list">
          <div className="user-settings-row">
            <span className="user-settings-copy">
              <b>Show my own sessions in the sessions list only to me</b>
              <span>
                Test calls and legitimate conversations you start while signed in as this user stay on
                your Sessions list. Other operators do not see those rows.
              </span>
            </span>
            {loading ? (
              <Loader2 className="user-settings-spin" size={18} />
            ) : (
              <Switch
                checked={hideOwn}
                disabled={saving}
                onCheckedChange={(v) => {
                  setHideOwn(v);
                  void persist({ hideOwnSessionsFromOthers: v });
                }}
                aria-label="Show my own sessions in the sessions list only to me"
              />
            )}
          </div>
        </Card>

        <Card className="settings-card" title="Language and region">
          <p className="user-settings-soon">Coming soon — placeholders only.</p>
          <div className="settings-fields">
            <div className="setting-field">
              <Select
                label="Display language"
                triggerClassName="setting-input"
                searchable={false}
                value="English"
                onChange={() => {}}
                options={[{ value: "English", label: "English" }]}
                disabled
              />
              <small>Admin UI language. Not persisted yet.</small>
            </div>
            <div className="setting-field">
              <Select
                label="Time zone"
                triggerClassName="setting-input"
                searchable={false}
                value="Use workspace default"
                onChange={() => {}}
                options={[{ value: "Use workspace default", label: "Use workspace default" }]}
                disabled
              />
              <small>
                <Globe size={12} /> Personal override of workspace timezone. Not persisted yet.
              </small>
            </div>
          </div>
        </Card>

        <Card className="settings-card" title="Notifications">
          <p className="user-settings-soon">Coming soon — in-product alerts are not wired.</p>
          <div className="user-settings-row muted">
            <span className="user-settings-copy">
              <b>Email digests</b>
              <span>Session and checkup summaries to this address.</span>
            </span>
            <Switch disabled checked={false} aria-label="Email digests (coming soon)" />
          </div>
          <div className="user-settings-row muted">
            <span className="user-settings-copy">
              <b>
                <Bell size={14} /> In-app notifications
              </b>
              <span>Bell on the top bar. Workspace chrome only today.</span>
            </span>
            <Switch disabled checked={false} aria-label="In-app notifications (coming soon)" />
          </div>
        </Card>

        <Card className="settings-card" title="Security">
          <p className="user-settings-soon">
            <Shield size={14} /> Password reset stays on the login screen. MFA and session devices will
            land here later.
          </p>
        </Card>
      </div>
    </>
  );
}
